/* eslint-disable react/jsx-one-expression-per-line */
import React from 'react';
import { Button } from '@blueprintjs/core';
import {
  Box,
  Card,
  Flex,
  FlexColumn,
  Heading,
  LinkButton,
  Tab,
  Tabs,
  Text,
  ThemedImage,
  Typewriter
} from 'core/components';
import styled from 'styled-components';
import { isEmpty, orderBy } from 'lodash';
import { getHashForObject } from 'app/stores/query/urlHash';
import MetricsExplorerResult from 'app/views/metrics/MetricsExplorerResult';
import MetricsExplorerLink from 'app/views/metrics/MetricsExplorerLink';
import { inject, observer } from 'mobx-react';
import ConnectivityReport from 'app/views/cloudConnectivity/ConnectivityReport';
import { formatDateTime } from 'core/util/dateUtils';
import kentikAiUser from 'app/assets/kentik-ai-user.svg';
import kentikAiUserDark from 'app/assets/kentik-ai-user-dark.svg';
import Gravatar from 'react-gravatar';
import JourneysDataView from 'app/views/core/journeys/JourneysDataView';
import { ViewInExplorerButton } from 'app/components/dataviews/tools';
import DataViewWrapper from '../../../components/dataviews/DataViewWrapper';
import SynResponse from './SynResponse';
import PromptFeedback from './PromptFeedback';
import Markdown from './Markdown';
import JourneyAlertResponse from './JourneyAlertResponse';
import PingResponse from './PingResponse';

const PromptHeader = styled(Flex)`
  align-items: center;
  border-radius: 8px;
  padding: 0;

  .bp4-button {
    opacity: 0.35;
  }

  :hover {
    .bp4-button {
      opacity: 1;
    }
  }
`;

export const ShimmerThinkingIndicator = styled.div`
  font-size: 13px;
  font-weight: 400;
  background: ${({ theme }) =>
    `linear-gradient(to right, ${theme.colors.body} 30%, #ed698a 45%, #76aace 50%, #ff8c2d 55%, ${theme.colors.body} 70%)`};
  background-size: 200% 100%;
  animation: gradientAnimation 2s linear infinite;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  @keyframes gradientAnimation {
    0% {
      background-position: 200% 50%;
    }
    100% {
      background-position: 0 50%;
    }
  }
`;

const User = ({ isSystemUser, userEmail }) =>
  isSystemUser ? (
    <ThemedImage src={kentikAiUser} darkSrc={kentikAiUserDark} width={40} height={40} />
  ) : (
    <Gravatar email={userEmail} size={40} default="initials" style={{ borderRadius: '8px' }} />
  );

const PromptWrapper = styled(Box)`
  scroll-snap-align: start;
  /* min-height: ${(props) => (props.isPlaceholder || props.isOpen ? '630px' : 'auto')}; */
`;

const KbAnswer = styled.div`
  line-height: 24px;

  pre {
    background: #4e4a67;
    padding: 1em;
    margin: 8px 0;
    border-radius: 6px;

    code {
      color: #ffffff;
      padding: 4px;
      border-radius: 4px;
    }
  }

  code {
    font-weight: 500;
    padding: 4px;
    border-radius: 4px;
  }

  th {
    background: ${(props) => props.theme.backgrounds.tableHeader};
  }
`;

function getRandomItem(array) {
  const randomIndex = Math.floor(Math.random() * array.length);
  return array[randomIndex];
}

const BAD_PROMPT_RESPONSES = [
  "Oops! Looks like we're having trouble coming up with a response for that prompt!",
  "Uh-oh! It seems we're having a bit of difficulty generating a response for your prompt.",
  'Having a little trouble generating a response for that one!',
  "Oh no! It appears we're encountering a hiccup in generating a response for your prompt.",
  "Whoops! We're facing a challenge in creating a response for that particular prompt."
];

@inject('$metrics', '$explorer')
@observer
class PromptResponse extends React.Component {
  promptRef = React.createRef();

  state = {
    updating: false
  };

  componentDidMount() {
    this.scrollIntoView();
  }

  componentDidUpdate(prevProps) {
    // when kentik ai is done thinking
    const { loading, isScrolledToBottom } = this.props;
    const { loading: prevLoading } = prevProps;

    if (loading !== prevLoading && isScrolledToBottom) {
      this.scrollIntoView();
    }
  }

  handleViewInCloudConnectivityChecker = (query) => {
    getHashForObject(query).then((hash) => {
      const path = `/v4/cloud/pathfinder/aws/${hash}`;
      window.open(path, '_blank');
    });
  };

  scrollIntoView = (force) => {
    const { isOpen, isScrolledToBottom } = this.props;

    if (this.promptRef.current && (force || (isOpen && isScrolledToBottom))) {
      setTimeout(() => {
        const targetElement = this.promptRef.current;

        if (!targetElement) {
          return;
        }

        const journeysScrollContainer = targetElement.parentElement.parentElement;

        journeysScrollContainer.scrollTo({
          top: this.promptRef.current.offsetTop - 20,
          behavior: 'smooth'
        });
      }, 200);
    }
  };

  onQueryError = (errors) => {
    const { updating } = this.state;
    const { query } = this.props;

    // Prevent a state/render infinite loop, and only needs to write to the DB on first render.
    if (!updating && query.get('is_query_valid')) {
      this.setState({ updating: true }, () => {
        query.save(
          { is_query_valid: false, admin_comment: JSON.stringify(errors) },
          { sendProvidedValuesOnly: true, toast: false }
        );
      });
    }
  };

  getResults = () => {
    const { model, query } = this.props;
    const models = model.get('models');
    if (models.length > 1) {
      return (
        <FlexColumn flex={1} position="relative" overflow="hidden" width="100%">
          <Tabs fill>
            {orderBy(models, (m) => m.get('model_id')).map((m) => (
              <Tab key={m.id} id={m.id} title={m.get('model_id')} panel={this.getResult(m)} />
            ))}
          </Tabs>
        </FlexColumn>
      );
    }
    return this.getResult(query);
  };

  // If a user selects FPA analysis options from the chart:
  // - Insert a new prompt with the FPA analysis options selected
  handleRunFpaFromChartSelection = (queryObject) => {
    const { journey, query } = this.props;
    const promptText = query.get('prompt');

    const {
      starting_time,
      ending_time,
      fpa: { time }
    } = queryObject;

    const prompt = `${promptText} from ${starting_time} to ${ending_time}. Run cause analysis from ${formatDateTime(
      time.starting_time
    )} to ${formatDateTime(time.ending_time)}`;

    journey.addPrompt(prompt);
  };

  onDataViewCreate = (dataview) => {
    console.warn('onDataViewCreate', dataview);
  };

  onNmsComplete = ({ results, query }) => {
    const { query: model } = this.props;
    model.setNmsCsv(results, query);
  };

  checkForReplay(prompt) {
    const query = prompt.get('query');
    return query.answer?.includes('replay_last');
  }

  getResult = (query) => {
    const { journey, loading, model } = this.props;

    const replayQuery = this.checkForReplay(query);
    const isMetricsQuery = query.get('app_protocol') === 16;
    const isFlowQuery = query.get('app_protocol') === 0;
    const isAskKb = query.get('app_protocol') === 99;
    const isAlerting = query.get('app_protocol') === 98;
    const isCloudConnectivityChecker = query.get('app_protocol') === 97;
    const isSynthetics = query.get('app_protocol') === 10;
    const isLiveSynth = query.get('app_protocol') === 82 || !!replayQuery;
    const isRemoving = model.isRequestActive('destroying');

    // TODO: Please move this to the backend!
    if (replayQuery) {
      const replayAppProtocol = parseInt(JSON.parse(query.get('query').answer).replay_last);
      query = query.collection.models.findLast((m) => m.get('app_protocol') === replayAppProtocol) || query;
    }

    const queryToRun = query.get('query');

    const promptHeight = 500;
    const chartHeight = 250;
    const toolsHeight = 42;

    const { use_fpa } = queryToRun;
    const legendFlex = use_fpa ? 2 : 1;

    if (loading) {
      return null;
    }

    if (isEmpty(query.get('query'))) {
      return [
        <FlexColumn key="error" flex={1} position="relative" overflow="hidden" p={2}>
          <Text as="div" muted>
            <Typewriter text={getRandomItem(BAD_PROMPT_RESPONSES)} />
          </Text>
        </FlexColumn>
      ];
    }

    if (isFlowQuery) {
      return [
        <DataViewWrapper
          key="dataview"
          query={query.get('query')}
          isExport
          fitToHeight
          isVirtualized={false}
          onDataViewCreate={this.onDataViewCreate}
          headerProps={{
            showTitle: false,
            showTitleLink: false,
            showViewTypeTag: true,
            shouldArrangeVertically: false,
            showLastUpdated: false,
            showDateRange: true,
            showAppliedFilters: true,
            showDataSources: true,
            isDashboardView: true // makes the title size smaller
          }}
          onSelectFpaAnalysisOption={this.handleRunFpaFromChartSelection}
          viewProps={{
            showNativeLegend: false,
            height: chartHeight
          }}
        >
          {(props) => (
            <JourneysDataView
              query={query}
              onQueryError={this.onQueryError}
              legendFlex={legendFlex}
              minHeight={chartHeight + toolsHeight}
              {...props}
            />
          )}
        </DataViewWrapper>,

        <ViewInExplorerButton
          key="tool"
          minimal={false}
          small={false}
          style={{ minWidth: 185, fontSize: 14 }}
          disabled={isRemoving}
          openInNewWindow
          query={query.get('query')}
          icon={null}
        />
      ];
    }

    if (isMetricsQuery) {
      return [
        <MetricsExplorerResult
          key="me-result"
          query={queryToRun}
          chartHeight={chartHeight}
          showLegend
          showTools
          isWidget
          isExport
          onQueryComplete={this.onNmsComplete}
        />,

        <MetricsExplorerLink
          key="tool"
          as={LinkButton}
          disabled={isRemoving}
          query={queryToRun}
          style={{ minWidth: 195 }}
          blank
        >
          View in Metrics Explorer
        </MetricsExplorerLink>
      ];
    }

    if (isCloudConnectivityChecker) {
      return [
        <Flex key="cloud-connectivity" flexDirection="column" overflow="auto" minHeight={promptHeight} p={2}>
          {queryToRun?.error_message ? (
            <Text as="div">{queryToRun.error_message}</Text>
          ) : (
            <ConnectivityReport reportObject={queryToRun} />
          )}
        </Flex>,
        <Button
          key="tool"
          text="View Report"
          disabled={isRemoving}
          style={{ minWidth: 185 }}
          onClick={() => this.handleViewInCloudConnectivityChecker(query.get('query'))}
        />
      ];
    }

    if (isLiveSynth) {
      return [<PingResponse key="ping" model={query} results={queryToRun} />];
    }

    if (isAskKb) {
      return [
        <Flex key="ask-kb" flexDirection="column" p={2}>
          {query.get('query.sources')?.length > 0 ? (
            <>
              <Heading level={6}>References</Heading>
              <Flex flexDirection="column" gap="6px">
                {query.get('query.sources')?.map((source) => (
                  <Text as="div" key={source.url}>
                    <Text muted pr="4px">
                      ({Math.round(source.similarity * 100)}%){' '}
                    </Text>
                    <a href={source.url} target="_blank" rel="noreferrer">
                      {source.article ? `${source.article} - ${source.heading}` : source.url}
                    </a>
                  </Text>
                ))}
              </Flex>
              <Text as="div" mt={2} muted small>
                AI can make mistakes. Validate important information using references or{' '}
                <a href="https://www.kentik.com/customer-care/">contact Kentik support.</a>
              </Text>
            </>
          ) : null}
        </Flex>
      ];
    }

    if (isAlerting) {
      return [
        <Flex key="cloud-connectivity" flexDirection="column" overflow="auto" minHeight={promptHeight}>
          <JourneyAlertResponse key="alert-response" journey={journey} query={query} />
        </Flex>
      ];
    }

    if (isSynthetics) {
      return [<SynResponse key="syn-response" query={query} />];
    }

    return [
      <Flex key="error" flexDirection="column" mb={2}>
        <Box>{query.get('completion.error') || query.get('completion.message')}</Box>
      </Flex>
    ];
  };

  renderResponse() {
    const { query, journey, onDelete } = this.props;
    const isAskKb = query.get('app_protocol') === 99;
    const hasQuery = query.get('query') && !isEmpty(query.get('query'));
    const isConnectivityChecker = query.get('app_protocol') === 97;
    const isEmptyAnswer = isAskKb && query.get('query.answer') === '';

    const isSystemUser = query.get('user.user_full_name') === 'Kentik AI';

    if ((!isSystemUser || isEmptyAnswer) && !hasQuery) {
      return null;
    }

    const [responseComponent, responseTools] = this.getResults();
    const WrapperComponent = isAskKb ? Box : Card;
    const maxHeight = isConnectivityChecker ? 'none' : 560;
    const wrapperProps = isAskKb
      ? { p: 0, mt: 0, ml: '40px' }
      : { display: 'flex', flexDirection: 'column', p: 0, mt: 3, maxHeight };

    return (
      <WrapperComponent {...wrapperProps}>
        <Flex flex={1} flexDirection="column" overflow="auto">
          {responseComponent}
        </Flex>
        <Flex alignItems="center" flexWrap="wrap" justifyContent="space-between" p="12px">
          <PromptFeedback journey={journey} model={query} onDelete={onDelete} />
          {responseTools}
        </Flex>
      </WrapperComponent>
    );
  }

  render() {
    const { query, loading, journey, onDelete } = this.props;
    const isPlaceholder = query.get('placeholder');
    const isSystemUser = query.get('user.user_full_name') === 'Kentik AI';
    const userEmail = query.get('user.user_email');
    const promptText = query.get('prompt');

    return (
      <PromptWrapper key={query.id} ref={this.promptRef} isOpen isPlaceholder={isPlaceholder}>
        <PromptHeader p="12px" mb={0} gap={1}>
          <Flex alignItems="flex-start" gap={2}>
            <User isSystemUser={isSystemUser} userEmail={userEmail} />
            <Flex flexDirection="column" pt="1px" gap="2px" flex={1}>
              <Flex alignItems="center" gap={1}>
                <Text fontSize={13} fontWeight="bold">
                  {query.get('user.user_full_name')}
                </Text>
                <Text muted fontSize={13} fontWeight="normal">
                  {formatDateTime(query.get('cdate', ''), 'MMM DD, YYYY, hh:mm a')}
                </Text>
              </Flex>
              {loading && <ShimmerThinkingIndicator>{loading}</ShimmerThinkingIndicator>}
              {!loading && promptText && (
                <KbAnswer fontSize={15}>
                  <Markdown allowMermaid={false}>{promptText}</Markdown>
                </KbAnswer>
              )}
            </Flex>
          </Flex>
        </PromptHeader>
        {loading && (
          <Flex alignItems="center" flexWrap="wrap" justifyContent="space-between" ml="40px" p="12px">
            <PromptFeedback journey={journey} model={query} onDelete={onDelete} />
          </Flex>
        )}
        {!loading && this.renderResponse()}
      </PromptWrapper>
    );
  }
}

export default PromptResponse;
